import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal, message, Select } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CommonTable from '../../components/commonTable';
import SearchBar from '../../components/searchBar';
import ColumnVisibility from '../../components/columnVisibility';
import CustomPagination from '../../components/pagination';
import DateRangeFilter from '../../components/dateRangeFilter';
import StatCard from '../../components/dashboards/StatCard';
import usePermissions from '../../hooks/usePermissions';
import { useGetPartialInvoicesQuery, useDeleteInvoiceMutation } from '../../services/invoice';
import { useGetUsersByRoleQuery } from '../../services/user';
import { getPartialInvoicesColumns } from './columns';
import './styles.scss';

/* ─── Constants ──────────────────────────────────────────────────────── */

const TYPE_OPTIONS = [
  { label: 'Personal Training', value: 'personal_training' },
  { label: 'Pilates',           value: 'pilates'           },
  { label: 'Therapy',           value: 'therapy'           },
  { label: 'EMS',               value: 'ems'               },
  { label: 'Paid Locker',       value: 'paid_locker'       },
  { label: 'MMA',               value: 'mma'               },
];

const SERVICE_FILTER_OPTIONS = [
  { label: 'All Service', value: 'all' },
  ...TYPE_OPTIONS,
];

/* ─── Component ──────────────────────────────────────────────────────── */

const PartialInvoice = () => {
  const navigate  = useNavigate();
  const branchId  = useSelector((state: any) => state.branch.selectedBranch);
  const { hasPermission } = usePermissions();

  const [page,              setPage]              = useState(1);
  const [limit,             setLimit]             = useState(10);
  const [searchText,        setSearchText]        = useState('');
  const [dateRange,         setDateRange]         = useState<{ start: Date; end: Date } | null>(null);
  const sortOrder                                  = 'desc';
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [salesPersonFilter, setSalesPersonFilter] = useState('all');
  const [visibleColumns,    setVisibleColumns]    = useState<Record<string, boolean>>({
    branchName:     true,
    invoiceNumber:  true,
    invoiceDate:    true,
    paymentDate:    true,
    customerName:   true,
    customerMobile: true,
    serviceType:    true,
    packageName:    true,
    salesPersonName: true,
    trainerName:    true,
    status:         true,
    packagePrice:   true,
    itemsPrice:     true,
    discount:       true,
    totalAmount:    true,
    dueAmount:      true,
    paymentMode:    true,
    paymentType:    true,
    gstNumber:      true,
    startDate:      true,
    expiryDate:     true,
    view:           true,
    actions:        true,
  });

  const { data: invoicesData, isLoading, error } = useGetPartialInvoicesQuery({
    page,
    limit,
    search:      searchText,
    branchId:    branchId || '',
    startDate:   dateRange?.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : '',
    endDate:     dateRange?.end   ? dayjs(dateRange.end).format('YYYY-MM-DD')   : '',
    sortOrder,
    invoiceType: 'pending',
    serviceType: serviceTypeFilter !== 'all' ? serviceTypeFilter : '',
    salesPerson: salesPersonFilter,
  } as any);

  const [deleteInvoice] = useDeleteInvoiceMutation();

  const { data: salesPersonsData } = useGetUsersByRoleQuery({ role: 'sales_representative', branchId: branchId || undefined } as any);

  const salesPersonOptions = [
    { value: 'all', label: 'All Sales Persons' },
    ...(((salesPersonsData as any)?.data ?? []) as any[]).map((p: any) => ({
      value: p.name || '',
      label: p.name || 'Unknown',
    })),
  ];

  const filteredData = useMemo<any[]>(
    () => (invoicesData as any)?.data || [],
    [(invoicesData as any)?.data],
  );
  const totalDueAmount = (invoicesData as any)?.summary?.totalDueAmount ?? 0;

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  const handleView = (record: any) => {
    console.log('View partial invoice:', record);
  };

  const handleEdit = (record: any) => {
    navigate(`/edit-invoice/${record._id}`);
  };

  const handleDelete = (record: any) => {
    Modal.confirm({
      title:      'Delete Partial Invoice',
      content:    `Are you sure you want to delete invoice "${record.invoiceNumber}"?`,
      okText:     'Delete',
      okType:     'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await (deleteInvoice as any)(record._id).unwrap();
          message.success('Partial invoice deleted successfully!');
        } catch {
          message.error('Failed to delete partial invoice');
        }
      },
    });
  };

  const allColumns: any[] = getPartialInvoicesColumns(
    handleEdit, handleDelete, handleView, 'dark',
    hasPermission('24-3-edit'),
    hasPermission('24-3-delete'),
  );
  const columns = allColumns.filter((col: any) => visibleColumns[col.key]);

  return (
    <div className="all-partial-invoices-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar value={searchText} onChange={setSearchText} placeholder="Search partial invoices..." />
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </div>
        <div className="right-col">
          <Select
            value={serviceTypeFilter}
            onChange={setServiceTypeFilter}
            className="status-filter"
            style={{ height: 41 }}
            options={SERVICE_FILTER_OPTIONS}
          />
          <Select
            value={salesPersonFilter}
            onChange={(val: string) => { setSalesPersonFilter(val); setPage(1); }}
            className="status-filter"
            style={{ height: 41, minWidth: 170 }}
            showSearch
            optionFilterProp="label"
            options={salesPersonOptions}
          />
          <ColumnVisibility
            columns={allColumns}
            visibleColumns={visibleColumns}
            onColumnToggle={handleColumnToggle}
          />
        </div>
      </div>

      <div className="partial-invoices-stats">
        <StatCard title="Total Due Amount" value={`₹${totalDueAmount.toLocaleString('en-IN')}`} icon={<ExclamationCircleOutlined />} color="#ff4d4f" />
      </div>

      <div className="partial-invoices-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={filteredData}
          loading={isLoading}
          rowKey={(record: any) => record._id || record.invoiceNumber}
          scroll={{ x: 'max-content' }}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={(invoicesData as any)?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />

      {error && (
        <div className="error-state">
          <p>Error loading partial invoices: {(error as any)?.message || 'Something went wrong'}</p>
        </div>
      )}
    </div>
  );
};

export default PartialInvoice;
